import * as THREE from 'three';
import { Viewer } from '../viewer/viewer';
import { Utils } from '../utils';

/** The Object Editor's handle look (Test mode): accent fill, dark outline and arrows. */
const FILL = '#B5FFA1'; // neon-green-300
const INK = '#111827'; // gray-900
/** Texture resolution: the disc is drawn once, at a size that stays crisp on high-DPI screens. */
const TEXTURE_PX = 128;

/** One shared texture: a disc with a double arrow along +X. */
let sharedTexture: THREE.CanvasTexture | null = null;

function handleTexture(): THREE.CanvasTexture {
  if (sharedTexture) return sharedTexture;
  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_PX;
  canvas.height = TEXTURE_PX;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    // Proportions of the editor's handle: r 8, outline 1.5, arrow ±4 with 2px heads, stroke 1.2.
    const c = TEXTURE_PX / 2;
    const unit = (TEXTURE_PX / 2 - 2) / 8.75;
    ctx.beginPath();
    ctx.arc(c, c, 8 * unit, 0, Math.PI * 2);
    ctx.fillStyle = FILL;
    ctx.fill();
    ctx.lineWidth = 1.5 * unit;
    ctx.strokeStyle = INK;
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(c - 4 * unit, c);
    ctx.lineTo(c + 4 * unit, c);
    ctx.moveTo(c - 2 * unit, c - 2 * unit);
    ctx.lineTo(c - 4 * unit, c);
    ctx.lineTo(c - 2 * unit, c + 2 * unit);
    ctx.moveTo(c + 2 * unit, c - 2 * unit);
    ctx.lineTo(c + 4 * unit, c);
    ctx.lineTo(c + 2 * unit, c + 2 * unit);
    ctx.lineWidth = 1.2 * unit;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  }
  sharedTexture = new THREE.CanvasTexture(canvas);
  sharedTexture.colorSpace = THREE.SRGBColorSpace;
  return sharedTexture;
}

/**
 * A drag handle on a selected object, drawn like the Object Editor's Test-mode
 * handles (a green disc with a double arrow showing the drag direction), a bit
 * bigger. It lies in the horizontal plane at a constant pixel size;
 * {@link setDirection} turns the arrow to the drag direction. Same API as
 * DoubleArrowMarker.
 */
export class RoundHandleMarker extends THREE.Object3D {
  private readonly viewer: Viewer;
  private readonly pixelDiameter: number;
  /** Cap (world units) on the diameter when zoomed far out, so it never dwarfs the object. */
  private maxWorldLength = Infinity;
  private readonly mesh: THREE.Mesh;
  private readonly material: THREE.MeshBasicMaterial;
  private readonly boundUpdateScale = (): void => this.updateScale();

  /** 24 px: the Object Editor's handles are 16 px, these sit on a busier plan. */
  constructor(viewer: Viewer, pixelDiameter = 24) {
    super();
    this.viewer = viewer;
    this.pixelDiameter = pixelDiameter;
    // Flat, unlit and always on top, like the other overlay markers.
    this.material = new THREE.MeshBasicMaterial({ map: handleTexture(), transparent: true, depthTest: false, depthWrite: false });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this.material);
    this.add(this.mesh);
    // No size until the first updateScale (it needs a world position).
    this.scale.set(0, 0, 0);
    viewer.addEventListener('update', this.boundUpdateScale);
  }

  /** Cap the diameter (world units); Infinity uncaps. */
  setMaxWorldLength(max: number): void {
    this.maxWorldLength = max > 0 ? max : Infinity;
  }

  setRenderOrder(order: number): void {
    this.renderOrder = order;
    this.mesh.renderOrder = order;
  }

  /** Point the arrow along `dir` (projected onto the horizontal plane); a near-zero direction is ignored. */
  setDirection(dir: THREE.Vector3): void {
    if (dir.x * dir.x + dir.y * dir.y < 1e-9) return;
    this.rotation.set(0, 0, Math.atan2(dir.y, dir.x));
  }

  /** Recompute the constant-pixel size now (call after positioning a marker shown mid-frame). */
  updateScale(): void {
    const camera = this.viewer.scene.getActiveCamera();
    const size = this.viewer.renderer.getSize(new THREE.Vector2());
    const distance = camera.position.distanceTo(this.getWorldPosition(new THREE.Vector3()));
    const pr = Utils.projectedRadius(1, camera, distance, size.width, size.height);
    const scale = Math.min(pr > 1e-6 ? this.pixelDiameter / pr : 1, this.maxWorldLength);
    this.scale.set(scale, scale, scale);
  }

  dispose(): void {
    this.viewer.removeEventListener('update', this.boundUpdateScale);
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}
